import React, { useState, useEffect, useCallback } from 'react';
import { commissionsAPI } from '../services/api';
import { FiRefreshCw, FiAlertTriangle, FiDollarSign, FiX, FiTrash2 } from 'react-icons/fi';
import './Students.css';
import './ClientVertical.css';

const STATUS_COLORS = {
  PENDING_RECEIPT: '#f5a623',
  RECEIVED: '#4facfe',
  AGENCY_PAID: '#43e97b',
};

const STATUS_LABELS = {
  PENDING_RECEIPT: 'Pending Receipt',
  RECEIVED: 'Received',
  AGENCY_PAID: 'Agency Paid',
};

const money = (v) => (v === null || v === undefined ? '—' : `$${parseFloat(v).toLocaleString()}`);

const Commissions = () => {
  const [records, setRecords] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amountDrafts, setAmountDrafts] = useState({});
  const [actionLoading, setActionLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rRes, repRes] = await Promise.all([
        commissionsAPI.list(),
        commissionsAPI.agencyReport(),
      ]);
      setRecords(rRes.data.results || rRes.data || []);
      setReport(repRes.data);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load commission records.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveAmount = async (record) => {
    const draft = amountDrafts[record.id];
    if (draft === undefined || draft === '') return;
    setActionLoading(record.id);
    try {
      await commissionsAPI.update(record.id, { university_commission_amount: draft });
      setAmountDrafts((prev) => ({ ...prev, [record.id]: undefined }));
      load();
    } catch (e) {
      setError('Failed to save amount.');
    } finally {
      setActionLoading(null);
    }
  };

  const doAction = async (record, action) => {
    setActionLoading(record.id);
    try {
      if (action === 'received') await commissionsAPI.markReceived(record.id);
      else await commissionsAPI.markAgencyPaid(record.id);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Action failed.');
    } finally {
      setActionLoading(null);
    }
  };

  const deleteRecord = async (record) => {
    const paidWarning = record.status === 'AGENCY_PAID'
      ? ` The agency share (${money(record.agency_share_amount)}) is already marked as paid out.`
      : '';
    if (!window.confirm(`Permanently delete the commission record for ${record.student_name}?${paidWarning} It will disappear from the agency totals. This cannot be undone.`)) return;
    setActionLoading(record.id);
    try {
      await commissionsAPI.delete(record.id);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to delete commission record.');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="students-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiDollarSign size={22} color="#011ff3" />
            Commissions
          </h1>
          <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14 }}>
            Auto-opened per enrollment — enter the amount received and the agency split is calculated automatically.
          </p>
        </div>
        <button className="btn-icon" onClick={load} title="Refresh">
          <FiRefreshCw size={15} className={loading ? 'cv-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <FiAlertTriangle size={18} />
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}>
            <FiX size={16} />
          </button>
        </div>
      )}

      {/* Per-agency summary cards */}
      {report && report.agencies.length > 0 && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {report.agencies.map((a) => (
            <div key={a.agency_id} className="cv-subsection" style={{ minWidth: 230, margin: 0 }}>
              <div style={{ color: '#1e293b', fontWeight: 600, fontSize: 14 }}>{a.agency}</div>
              <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 8 }}>{a.country} · {a.split_percent != null ? `${a.split_percent}% split` : 'no split set'} · {a.enrollments} enrollment(s)</div>
              <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.8 }}>
                Commission: <strong style={{ color: '#1e293b' }}>{money(a.total_university_commission)}</strong><br />
                VEC share: <strong style={{ color: '#43e97b' }}>{money(a.vec_share_total)}</strong> ·
                Owed: <strong style={{ color: '#f5a623' }}>{money(a.agency_share_owed)}</strong> ·
                Paid: <strong style={{ color: '#4facfe' }}>{money(a.agency_share_paid)}</strong>
              </div>
            </div>
          ))}
          <div className="cv-subsection" style={{ minWidth: 200, margin: 0 }}>
            <div style={{ color: '#1e293b', fontWeight: 600, fontSize: 14 }}>In-House</div>
            <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 8 }}>{report.in_house.enrollments} enrollment(s)</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Commission: <strong style={{ color: '#1e293b' }}>{money(report.in_house.total_university_commission)}</strong>
            </div>
          </div>
        </div>
      )}

      <div className="students-table-container">
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : records.length === 0 ? (
          <div className="empty-state">No commission records yet — they appear automatically when a student enrolls.</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                {['Student', 'University', 'Agency', 'Commission (USD)', 'Agency Share', 'VEC Share', 'Status', 'Actions'].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {records.map((r) => {
                const color = STATUS_COLORS[r.status] || '#94a3b8';
                const busy = actionLoading === r.id;
                return (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.student_name}</strong>
                      <div style={{ color: '#94a3b8', fontSize: 11, fontFamily: 'monospace' }}>{r.student_reference}</div>
                    </td>
                    <td>{r.university || '—'}</td>
                    <td>
                      {r.agency_name || <span style={{ color: '#94a3b8' }}>In-house</span>}
                      {r.agency_split_percent != null && (
                        <div style={{ color: '#94a3b8', fontSize: 11 }}>{parseFloat(r.agency_split_percent)}% split</div>
                      )}
                    </td>
                    <td>
                      {r.status === 'PENDING_RECEIPT' ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input
                            type="number" min="0" step="0.01"
                            value={amountDrafts[r.id] ?? (r.university_commission_amount || '')}
                            onChange={(e) => setAmountDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))}
                            placeholder="0.00"
                            style={{ width: 90, border: '1px solid #e0e0e0', borderRadius: 6, padding: '5px 8px', fontSize: 12 }}
                          />
                          <button onClick={() => saveAmount(r)} disabled={busy} className="cv-inline-btn">Save</button>
                        </div>
                      ) : (
                        <strong>{money(r.university_commission_amount)}</strong>
                      )}
                    </td>
                    <td style={{ color: '#f5a623' }}>{money(r.agency_share_amount)}</td>
                    <td style={{ color: '#43e97b' }}>{money(r.vec_share_amount)}</td>
                    <td><span className="status-pill" style={{ color }}>{STATUS_LABELS[r.status] || r.status}</span></td>
                    <td>
                      {r.status === 'PENDING_RECEIPT' && r.university_commission_amount && (
                        <button onClick={() => doAction(r, 'received')} disabled={busy} className="cv-inline-btn" style={{ color: '#4facfe' }}>
                          Mark Received
                        </button>
                      )}
                      {r.status === 'RECEIVED' && (
                        r.agency_name ? (
                          <button onClick={() => doAction(r, 'paid')} disabled={busy} className="cv-inline-btn" style={{ color: '#43e97b' }}>
                            Pay Agency Share
                          </button>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>No payout needed</span>
                        )
                      )}
                      <button
                        onClick={() => deleteRecord(r)}
                        disabled={busy}
                        className="btn-icon btn-danger"
                        title="Delete commission record"
                        style={{ marginLeft: 8, verticalAlign: 'middle' }}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Commissions;
